import { useMemo, useState } from "react";
import { CalendarDays } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useUrlParam } from "@/hooks/useUrlParam";

const RANGES: Array<{ value: string; label: string; days: number | null }> = [
  { value: "any", label: "Any date", days: null },
  { value: "7", label: "Last 7 days", days: 7 },
  { value: "30", label: "Last 30 days", days: 30 },
  { value: "90", label: "Last 90 days", days: 90 },
];

const RANGE_VALUES = RANGES.map((r) => r.value);

export function useDateRangeFilter(opts: { syncUrl?: boolean } = {}) {
  const [localRange, setLocalRange] = useState("any");
  const [urlRange, setUrlRange] = useUrlParam("range", "any", RANGE_VALUES);
  const range = opts.syncUrl ? urlRange : localRange;
  const setRange = opts.syncUrl ? setUrlRange : setLocalRange;

  const since = useMemo(() => {
    const days = RANGES.find((r) => r.value === range)?.days;
    if (!days) return undefined;
    const d = new Date();
    d.setDate(d.getDate() - days);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }, [range]);

  return { range, setRange, since, active: range !== "any" };
}

export function DateRangeFilter({
  value,
  onChange,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={cn("w-[160px]", className)}>
        <div className="flex items-center gap-2 overflow-hidden">
          <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />
          <SelectValue />
        </div>
      </SelectTrigger>
      <SelectContent>
        {RANGES.map((r) => (
          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
